import type {
  MapRect,
  MapAreaData,
  AreaRespawnZone,
  AreaEntryRequirement,
} from '../types/map';
import type { CharacterClassId, CharacterRaceId } from '../types/character';

/**
 * Helpers PUROS (sin estado) para el sistema de áreas.
 * Compartidos entre server (gating/PvP/respawn) y cliente (editor).
 * Ver docs/AREAS.md.
 */

/** True si el tile (x,y) cae dentro del rectángulo. */
export function rectContains(r: MapRect, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}

/** Superficie (en tiles) de un conjunto de rectángulos. Puede sobrecontar overlaps. */
export function rectsArea(rects: MapRect[]): number {
  let total = 0;
  for (const r of rects) total += r.w * r.h;
  return total;
}

/** True si el tile (x,y) pertenece al área (alguno de sus rects lo contiene). */
export function areaContainsTile(area: MapAreaData, x: number, y: number): boolean {
  for (const r of area.rects) {
    if (rectContains(r, x, y)) return true;
  }
  return false;
}

/** Áreas que cubren el tile (x,y). */
export function getAreasAtTile(
  areas: MapAreaData[] | undefined,
  x: number,
  y: number
): MapAreaData[] {
  if (!areas || areas.length === 0) return [];
  return areas.filter((a) => areaContainsTile(a, x, y));
}

/**
 * Área-banner para un tile: la más chica (menor superficie) que tenga `name`.
 * Devuelve null si ninguna de las áreas del tile tiene nombre.
 */
export function pickBannerArea(areasAtTile: MapAreaData[]): MapAreaData | null {
  let best: MapAreaData | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const a of areasAtTile) {
    if (!a.name) continue;
    const size = rectsArea(a.rects);
    if (size < bestArea) {
      bestArea = size;
      best = a;
    }
  }
  return best;
}

/** Regla de overlap: el tile es seguro si CUALQUIER área que lo cubre es `safe`. */
export function isTileSafe(areasAtTile: MapAreaData[]): boolean {
  return areasAtTile.some((a) => a.safe === true);
}

/**
 * Regla de overlap: el tile permite botar/desembarcar el barco si CUALQUIER
 * área que lo cubre es `embarkable` (muelles). Ver docs/WATER_AND_BOATS.md.
 */
export function isTileEmbarkable(areasAtTile: MapAreaData[]): boolean {
  return areasAtTile.some((a) => a.embarkable === true);
}

/**
 * Regla de overlap: el drop de items al morir está DESACTIVADO en el tile si
 * CUALQUIER área que lo cubre lo desactiva (`dropItemsOnDeath === false`).
 * Ausente o `true` ⇒ dropea (default). Permite zonas "no-drop" sin tener que
 * marcarlas `safe` (un área puede ser insegura/PvP pero no hacerte perder loot).
 */
export function tileDropsItemsOnDeath(areasAtTile: MapAreaData[]): boolean {
  return !areasAtTile.some((a) => a.dropItemsOnDeath === false);
}

/** Categoría visual de una zona, para el indicador HUD (`area_<tipo>.png`). */
export type ZoneType = 'safe' | 'neutral' | 'especial' | 'danger';

/**
 * Tipo de zona del tile, DERIVADO de los flags de las áreas que lo cubren
 * (no hay campo propio). Precedencia (primer match gana, regla de overlap):
 *  1. alguna área `safe` ⇒ `safe` (verde): sin PvP ni drop.
 *  2. alguna área con `factionId` ⇒ `neutral` (mitad): territorio de facción.
 *  3. alguna área `dropItemsOnDeath === false` ⇒ `especial` (dorado): peligrosa
 *     pero sin pérdida de loot.
 *  4. resto, o **sin áreas (mundo abierto)** ⇒ `danger` (rojo): se cae todo.
 * Coherente con la lógica de drop al morir (ver docs/DEATH_AND_PRIEST.md).
 */
export function classifyZoneType(areasAtTile: MapAreaData[]): ZoneType {
  if (areasAtTile.some((a) => a.safe === true)) return 'safe';
  if (areasAtTile.some((a) => !!a.factionId)) return 'neutral';
  if (areasAtTile.some((a) => a.dropItemsOnDeath === false)) return 'especial';
  return 'danger';
}

/** Expande un conjunto de rects a su lista de tiles. */
export function rectsTiles(rects: MapRect[]): Array<{ x: number; y: number }> {
  const tiles: Array<{ x: number; y: number }> = [];
  for (const r of rects) {
    for (let dy = 0; dy < r.h; dy++) {
      for (let dx = 0; dx < r.w; dx++) {
        tiles.push({ x: r.x + dx, y: r.y + dy });
      }
    }
  }
  return tiles;
}

/** Tiles de una zona de respawn. */
export function zoneTiles(zone: AreaRespawnZone): Array<{ x: number; y: number }> {
  return rectsTiles(zone.rects);
}

/** Tiles de un área completa. */
export function areaTiles(area: MapAreaData): Array<{ x: number; y: number }> {
  return rectsTiles(area.rects);
}

/** Contexto del player para evaluar requisitos de entrada. */
export interface AreaEntryContext {
  /** Cantidad del item requerido que tiene el player (si aplica el chequeo). */
  itemCount: number;
  classId: CharacterClassId;
  raceId: CharacterRaceId;
  /** Hora local del server (0-23). */
  hour: number;
}

/** True si la hora `h` cae en la franja [start, end) con wraparound (ej: 22→6). */
export function hourInWindow(h: number, start: number, end: number): boolean {
  if (start === end) return true; // ventana vacía/total → sin restricción
  if (start < end) return h >= start && h < end;
  // wraparound (ej: 22 → 6): permitido si h>=start o h<end
  return h >= start || h < end;
}

/**
 * Evalúa si un contexto de player cumple los requisitos de entrada.
 * Un requisito ausente no restringe.
 */
export function meetsEntry(
  req: AreaEntryRequirement | undefined,
  ctx: AreaEntryContext
): boolean {
  if (!req) return true;

  if (req.itemId) {
    const needed = req.itemQuantity ?? 1;
    if (ctx.itemCount < needed) return false;
  }
  if (req.classes && req.classes.length > 0) {
    if (!req.classes.includes(ctx.classId)) return false;
  }
  if (req.races && req.races.length > 0) {
    if (!req.races.includes(ctx.raceId)) return false;
  }
  if (req.timeStartHour !== undefined && req.timeEndHour !== undefined) {
    if (!hourInWindow(ctx.hour, req.timeStartHour, req.timeEndHour)) return false;
  }
  return true;
}
